<template>
    <nav
        v-if="visible"
        class="brand-support-overview"
        :class="{ 'is-busy': switchingKey !== null }"
        aria-label="Чаты по компаниям"
    >
        <span
            v-if="!snapshot && status === 'loading'"
            class="brand-support-overview__loading"
            role="status"
            aria-label="Загружаем чаты по компаниям"
        >
            <span class="brand-support-overview__spin"></span>
        </span>

        <button
            v-else-if="!snapshot"
            type="button"
            class="brand-support-overview__more"
            title="Чаты по компаниям не загрузились. Нажмите, чтобы повторить"
            aria-label="Чаты по компаниям не загрузились. Повторить"
            @click="refresh"
        >
            —
        </button>

        <template v-else>
            <div v-show="!compact" ref="list" class="brand-support-overview__list">
                <company-shortcut
                    v-for="(company, index) in companies"
                    v-show="visibleCount === null || index < visibleCount"
                    :key="company.key"
                    :company="company"
                    :stale="isCompanyStale(company)"
                    :busy="switchingKey === company.key"
                    @open="openCompany"
                    @show-tip="showTip(company, $event)"
                    @hide-tip="hideTip"
                />
            </div>

            <button
                v-if="hiddenCompanies.length"
                ref="more"
                type="button"
                class="brand-support-overview__more"
                :class="{ 'is-compact': compact }"
                aria-haspopup="true"
                :aria-expanded="menuOpen ? 'true' : 'false'"
                :aria-label="moreLabel"
                :title="moreLabel"
                @click.stop="toggleMenu"
            >
                <template v-if="compact">
                    <svg aria-hidden="true" width="12" height="12"><use href="#icon-cloud-chat"></use></svg>
                    <span
                        class="brand-support-overview__count"
                        :class="{ 'is-pos': totalWaiting > 0 }"
                    >{{ totalWaiting }}</span>
                </template>
                <template v-else>+{{ hiddenCompanies.length }}</template>
            </button>

            <button
                v-if="!companies.length && canManage"
                ref="gear"
                type="button"
                class="brand-support-overview__add"
                aria-label="Настроить компании"
                @click="openSettings"
            >
                <svg aria-hidden="true" width="10" height="10"><use href="#icon-plus"></use></svg>
                Выбрать компании
            </button>
            <button
                v-else-if="canManage"
                ref="gear"
                type="button"
                class="brand-support-overview__icon"
                title="Настроить компании"
                aria-label="Настроить компании"
                @click="openSettings"
            >
                <svg aria-hidden="true" width="13" height="13"><use href="#icon-options"></use></svg>
            </button>
        </template>

        <div
            v-if="tip"
            class="brand-support-overview__tip"
            role="tooltip"
            :style="{ left: tip.left + 'px', top: tip.top + 'px' }"
        >
            <b>{{ tip.company.name }}</b>
            <span
                v-for="line in tipLines"
                :key="line.text"
                :class="{ 'is-warn': line.warn }"
            >{{ line.text }}</span>
        </div>

        <div
            v-if="menuOpen"
            ref="menu"
            class="brand-support-overview__menu"
            role="menu"
            :style="menuStyle"
            @click.stop
        >
            <div class="brand-support-overview__menu-list">
                <button
                    v-for="company in hiddenCompanies"
                    :key="company.key"
                    type="button"
                    role="menuitem"
                    class="brand-support-overview__menu-item"
                    :aria-disabled="company.canOpen ? null : 'true'"
                    @click="openCompany(company)"
                >
                    <span
                        class="brand-support-overview__count"
                        :class="{ 'is-pos': company.waiting > 0 && !isCompanyStale(company) }"
                    >{{ company.waiting === null ? '—' : company.waiting }}</span>
                    <span class="brand-support-overview__menu-name">
                        {{ company.name }}
                        <small>{{ menuHint(company) }}</small>
                    </span>
                    <span v-if="company.isCurrent" class="brand-support-overview__menu-current">сейчас</span>
                </button>
            </div>
            <div class="brand-support-overview__menu-foot">
                <span>{{ updatedText }}</span>
                <button
                    type="button"
                    class="button button_size_small button_color_transp-black"
                    @click="closeMenu(); refresh()"
                >
                    Обновить
                </button>
            </div>
        </div>

        <company-settings-modal
            v-if="settingsOpen"
            @close="closeSettings"
            @changed="handleSettingsChanged"
            @forbidden="handleSettingsForbidden"
        />
    </nav>
</template>

<script>
import api from '@api'
import { eventBus } from '@eventBus'
import CompanyShortcut from './CompanyShortcut'
import CompanySettingsModal from './CompanySettingsModal'
import { companiesWord, formatTime, waitingText } from './format'
import { errorStatus, normalizeSummary, retryAfterMs } from './contract'
import { isStale, nextDelay } from './polling'
import { detachFailCallback, isSwitching, startSwitch, takeAppliedSwitch } from './companySwitchAdapter'
import { closeChatBar, openCommonQueue, resetChatContext } from './chatQueue'

const CHIP_GAP = 4
const MORE_WIDTH = 38
const GEAR_WIDTH = 26
const SIDE_PADDING = 24
const TIP_WIDTH = 280

export default {
    name: 'BrandSupportOverview',
    components: {
        CompanyShortcut,
        CompanySettingsModal,
    },
    data() {
        return {
            status: 'idle',
            snapshot: null,
            failures: 0,
            now: Date.now(),
            timer: null,
            inFlight: false,
            cancelSource: null,
            generation: 0,
            requestSeq: 0,
            visibleCount: null,
            compact: false,
            tip: null,
            menuOpen: false,
            menuStyle: {},
            settingsOpen: false,
            switchingKey: null,
            pendingSwitch: null,
            resizeObserver: null,
            observedElement: null,
        }
    },
    computed: {
        capability() {
            const user = this.$store.state.profile.user
            const capabilities = user && user.capabilities
            return (capabilities && capabilities.brand_support_overview) || null
        },
        enabled() {
            const cap = this.capability
            return Boolean(cap && cap.enabled === true && (cap.can_view === true || cap.can_manage === true))
        },
        companies() {
            return this.snapshot ? this.snapshot.companies : []
        },
        canManage() {
            return Boolean(this.snapshot && this.snapshot.canManage)
        },
        visible() {
            if (!this.enabled || this.status === 'off') return false
            return !this.snapshot || this.companies.length > 0 || this.canManage
        },
        hiddenCompanies() {
            return this.visibleCount === null ? [] : this.companies.slice(this.visibleCount)
        },
        totalWaiting() {
            return this.companies.reduce((sum, company) => sum + (company.waiting || 0), 0)
        },
        moreLabel() {
            const count = this.hiddenCompanies.length
            if (this.compact) return `Чаты по компаниям: ${count} ${companiesWord(count)}, ждут оператора — ${this.totalWaiting}`
            return `Ещё ${count} ${companiesWord(count)} — число компаний, а не чатов`
        },
        updatedText() {
            if (!this.snapshot) return ''
            const time = formatTime(this.snapshot.receivedAt)
            return this.failures ? `Не обновлено · данные на ${time}` : `Обновлено в ${time}`
        },
        tipLines() {
            if (!this.tip) return []
            const company = this.tip.company
            const lines = [{ text: `${waitingText(company)}. По всей компании` }]
            if (company.warning) lines.push({ text: 'Часть данных посчитана неточно — число может отличаться', warn: true })
            if (this.isCompanyStale(company)) {
                const time = formatTime(company.asOf !== null ? company.asOf : this.snapshot.receivedAt)
                lines.push({ text: `Данные устарели · не обновлялись с ${time}`, warn: true })
            } else {
                lines.push({ text: this.updatedText })
            }
            if (!company.canOpen) lines.push({ text: 'Нет доступа к чатам этой компании', warn: true })
            else if (company.isCurrent) lines.push({ text: 'Вы сейчас в этой компании · нажмите, чтобы открыть очередь' })
            else lines.push({ text: 'Нажмите, чтобы перейти и открыть чаты' })
            return lines
        },
        layoutKey() {
            return this.companies.map(company => `${company.key}:${company.waiting}`).join('|') + `|${this.canManage}`
        },
    },
    watch: {
        enabled(value) {
            if (value) this.start()
            else this.shutdown()
        },
        layoutKey() {
            this.$nextTick(this.layout)
        },
    },
    mounted() {
        this.pendingSwitch = takeAppliedSwitch()
        document.addEventListener('visibilitychange', this.handleVisibility)
        document.addEventListener('click', this.closeMenu)
        document.addEventListener('keydown', this.handleKeydown)
        window.addEventListener('storage', this.handleStorage)
        window.addEventListener('resize', this.handleResize)
        eventBus.$on('companyisChanget', this.handleCompanyChanged)
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.handleResize)
        }
        if (this.enabled) this.start()
    },
    beforeDestroy() {
        this.invalidate(false)
        document.removeEventListener('visibilitychange', this.handleVisibility)
        document.removeEventListener('click', this.closeMenu)
        document.removeEventListener('keydown', this.handleKeydown)
        window.removeEventListener('storage', this.handleStorage)
        window.removeEventListener('resize', this.handleResize)
        eventBus.$off('companyisChanget', this.handleCompanyChanged)
        if (this.resizeObserver) this.resizeObserver.disconnect()
        detachFailCallback(this.handleSwitchFailed)
    },
    methods: {
        start() {
            this.status = 'loading'
            this.fetchSummary()
        },
        shutdown() {
            this.invalidate(true)
            this.status = 'idle'
            this.pendingSwitch = null
        },
        // Drops in-flight work; a late response from an older generation is ignored.
        invalidate(clearData) {
            this.generation++
            clearTimeout(this.timer)
            this.timer = null
            if (this.cancelSource) this.cancelSource.cancel()
            this.cancelSource = null
            this.inFlight = false
            if (clearData) {
                this.snapshot = null
                this.failures = 0
                this.visibleCount = null
                this.compact = false
                this.tip = null
                this.menuOpen = false
                this.settingsOpen = false
            }
        },
        schedule(delay) {
            clearTimeout(this.timer)
            this.timer = null
            if (document.hidden || this.switchingKey !== null) return
            this.timer = setTimeout(this.fetchSummary, delay)
        },
        refresh() {
            if (this.inFlight) return
            clearTimeout(this.timer)
            this.fetchSummary()
        },
        fetchSummary() {
            if (this.inFlight || !this.enabled || this.switchingKey !== null) return
            const generation = this.generation
            const seq = ++this.requestSeq
            const source = api.brandSupportOverview.createCancelSource()
            this.inFlight = true
            this.cancelSource = source
            api.brandSupportOverview.summary(source.token)
                .then(normalizeSummary)
                .then(summary => {
                    if (generation !== this.generation || seq !== this.requestSeq) return
                    this.applySummary(summary)
                })
                .catch(error => {
                    if (api.brandSupportOverview.isCancel(error)) return
                    if (generation !== this.generation || seq !== this.requestSeq) return
                    this.handleSummaryError(error)
                })
                .finally(() => {
                    if (seq !== this.requestSeq) return
                    this.inFlight = false
                    this.cancelSource = null
                })
        },
        applySummary(summary) {
            this.now = Date.now()
            // A full summary replaces the previous set: companies that disappeared are dropped with their numbers.
            this.snapshot = { ...summary, receivedAt: this.now }
            this.failures = 0
            this.status = 'ready'
            if (this.pendingSwitch) this.finishSwitch(summary)
            this.schedule(summary.pollIntervalMs)
        },
        handleSummaryError(error) {
            const status = errorStatus(error)
            this.now = Date.now()
            if (status === 401) return
            if (status === 403 || status === 404) {
                // Access revoked or module switched off: forget the numbers, stop polling.
                this.invalidate(true)
                this.status = 'off'
                this.dropPendingSwitch()
                return
            }
            this.failures++
            this.status = this.snapshot ? 'ready' : 'error'
            this.dropPendingSwitch()
            const pollMs = this.snapshot ? this.snapshot.pollIntervalMs : 15000
            this.schedule((status === 429 && retryAfterMs(error)) || nextDelay(this.failures, pollMs))
        },
        isCompanyStale(company) {
            return this.failures > 0 || isStale(company, this.snapshot, this.now)
        },

        layout() {
            if (!this.$refs.list) return
            if (this.resizeObserver && this.observedElement !== this.$el) {
                this.resizeObserver.disconnect()
                this.resizeObserver.observe(this.$el)
                this.observedElement = this.$el
            }
            this.visibleCount = null
            this.compact = false
            this.$nextTick(this.measure)
        },
        measure() {
            const list = this.$refs.list
            if (!list) return
            const widths = Array.from(list.children).map(chip => chip.offsetWidth + CHIP_GAP)
            const available = this.$el.clientWidth - SIDE_PADDING - (this.canManage ? GEAR_WIDTH : 0)
            const needed = widths.reduce((sum, width) => sum + width, 0)
            if (needed <= available) return
            let used = 0
            let shown = 0
            while (shown < widths.length && used + widths[shown] + MORE_WIDTH <= available) {
                used += widths[shown]
                shown++
            }
            this.visibleCount = shown < 2 ? 0 : shown
            this.compact = shown < 2
        },
        // ResizeObserver already reports at frame timing, so the layout runs right away.
        handleResize() {
            this.dismissOverlays()
            this.layout()
        },

        // The tooltip and the "more" menu are positioned from measured rects, so any
        // change that can move the chips closes both.
        dismissOverlays() {
            this.hideTip()
            this.closeMenu()
        },

        showTip(company, element) {
            const rect = element.getBoundingClientRect()
            const left = Math.min(Math.max(8, rect.left), window.innerWidth - TIP_WIDTH - 8)
            this.tip = { company, left, top: rect.bottom + 8 }
        },
        hideTip() {
            this.tip = null
        },
        menuHint(company) {
            return company.canOpen ? waitingText(company) : 'Нет доступа к чатам'
        },
        toggleMenu() {
            if (this.menuOpen) {
                this.closeMenu()
                return
            }
            const rect = this.$refs.more.getBoundingClientRect()
            this.hideTip()
            this.menuStyle = {
                top: `${rect.bottom + 6}px`,
                left: `${Math.min(Math.max(8, rect.left), window.innerWidth - 308)}px`,
            }
            this.menuOpen = true
            this.$nextTick(() => {
                const first = this.$refs.menu && this.$refs.menu.querySelector('button')
                if (first) first.focus()
            })
        },
        closeMenu() {
            this.menuOpen = false
        },
        handleKeydown(event) {
            if (event.key !== 'Escape' || !this.menuOpen) return
            this.closeMenu()
            if (this.$refs.more) this.$refs.more.focus()
        },
        handleVisibility() {
            if (!this.enabled || this.status === 'off') return
            if (document.hidden) {
                clearTimeout(this.timer)
                this.timer = null
            } else {
                this.refresh()
            }
        },
        // Another tab logged in again or switched company: the stored session changed under us.
        handleStorage(event) {
            if (event.key !== 'common' || !this.enabled) return
            this.invalidate(true)
            this.dropPendingSwitch()
            this.start()
        },
        handleCompanyChanged() {
            this.invalidate(true)
        },

        openSettings() {
            this.dismissOverlays()
            this.settingsOpen = true
        },
        closeSettings() {
            this.settingsOpen = false
            this.$nextTick(() => {
                if (this.$refs.gear) this.$refs.gear.focus()
            })
        },
        handleSettingsChanged() {
            // Newer settings: an older summary still in flight must not bring removed companies back.
            this.invalidate(false)
            this.fetchSummary()
        },
        handleSettingsForbidden() {
            this.settingsOpen = false
            this.handleSettingsChanged()
        },

        openCompany(company) {
            this.dismissOverlays()
            if (this.switchingKey !== null || isSwitching()) return
            if (!company.canOpen) {
                this.$toast.error(`Нет доступа к чатам «${company.name}»`)
                return
            }
            if (company.isCurrent) {
                this.openQueue()
                return
            }
            this.switchingKey = company.key
            this.invalidate(false)
            const generation = this.generation
            closeChatBar(this.$store)
            resetChatContext(this.$store)
            // Leave object pages first so they do not reload under the new company's session.
            this.leaveToNeutralRoute().then(() => {
                // beforeDestroy bumps the generation; a destroyed instance must not start the switch.
                if (generation !== this.generation) return
                if (!startSwitch(company, this.handleSwitchFailed)) {
                    this.switchingKey = null
                    this.fetchSummary()
                }
            })
        },
        leaveToNeutralRoute() {
            if (this.$route.name === 'dashboard') return Promise.resolve()
            return this.$router.push({ name: 'dashboard' }).catch(() => {})
        },
        handleSwitchFailed(intent) {
            if (intent.applied) {
                // New session headers are already applied but the page never switched over:
                // reload so the profile, permissions and chats are read for the actual session.
                window.location.reload()
                return
            }
            this.switchingKey = null
            this.$toast.error(`Не удалось перейти в «${intent.name}». Попробуйте ещё раз`)
            this.fetchSummary()
        },
        openQueue() {
            const perm = this.$store.getters.perm
            if (!perm || !perm.quick_chat_management) {
                this.$toast.error('В этой компании у вас нет доступа к чатам')
                return
            }
            openCommonQueue(this)
        },
        // Runs in the component created after the switch, once the new summary confirms the company.
        finishSwitch(summary) {
            const intent = this.pendingSwitch
            this.pendingSwitch = null
            const user = this.$store.state.profile.user
            const profileMatches = intent.profileId === null || !user || user.id === undefined
                || String(user.id) === intent.profileId
            if (summary.currentCompanyId !== intent.companyId || !profileMatches) {
                this.$toast.error('Компания переключена, но не удалось подтвердить переход. Чаты не открыты')
                return
            }
            this.openQueue()
        },
        dropPendingSwitch() {
            if (!this.pendingSwitch) return
            this.pendingSwitch = null
            this.$toast.error('Компания переключена, но не удалось загрузить данные. Чаты не открыты')
        },
    },
}
</script>

<style lang="scss">
@import '@scss/variables';

.brand-support-overview {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 12px;
    align-self: stretch;

    &.is-busy {
        .brand-support-overview__chip:not(.is-busy),
        .brand-support-overview__more,
        .brand-support-overview__icon {
            opacity: .5;
            pointer-events: none;
        }
    }

    &__list {
        display: flex;
        align-items: center;
        gap: 4px;
        min-width: 0;
    }

    &__loading {
        display: inline-flex;
        align-items: center;
        height: 22px;
        padding: 0 6px;
    }

    &__chip {
        position: relative;
        flex: none;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 22px;
        padding: 0 3px 0 6px;
        border: 1px solid $global-light-gray;
        border-radius: 7px;
        background: $global-white;
        color: $global-black;
        font: inherit;
        font-variant-numeric: tabular-nums;
        cursor: pointer;
        transition: border-color .15s, background-color .15s;

        &:hover {
            border-color: $global-gray-light;
            background: $global-white-f6;
        }

        &.is-current {
            border: 1.5px solid $global-black;
            padding-left: 5.5px;
            padding-right: 2.5px;
        }

        &.is-pos .brand-support-overview__count {
            background: $global-yellow-light;
            color: $global-black;
        }

        &.is-stale {
            border-style: dashed;
            border-color: $global-very-gray-84;

            .brand-support-overview__count {
                background: $global-white-f6;
                color: $global-very-gray-5b;
            }
        }

        &.is-unavail .brand-support-overview__count {
            background: repeating-linear-gradient(135deg, $global-white-f6 0 4px, $global-very-gray-dd 4px 6px);
            color: $global-very-gray-5b;
        }

        &.is-locked {
            cursor: not-allowed;

            .brand-support-overview__letter,
            .brand-support-overview__count {
                opacity: .4;
            }
        }

        &.is-busy {
            cursor: progress;
        }
    }

    &__chip:focus-visible,
    &__more:focus-visible,
    &__icon:focus-visible,
    &__add:focus-visible,
    &__menu-item:focus-visible {
        outline: 2px solid $global-purple;
        outline-offset: 2px;
    }

    &__letter {
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
    }

    &__count {
        min-width: 15px;
        height: 15px;
        padding: 0 4px;
        border-radius: 5px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 9px;
        font-weight: 600;
        line-height: 1;
        background: $global-white-f6;
        color: $global-very-gray-84;

        &.is-pos {
            background: $global-yellow-light;
            color: $global-black;
        }
    }

    &__mark {
        position: absolute;
        top: -3px;
        right: -3px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        border: 1.5px solid $global-white;
        box-sizing: content-box;

        &_warn {
            top: -2px;
            right: -2px;
            width: 5px;
            height: 5px;
            border-radius: 2px;
            background: $global-orange;
            transform: rotate(45deg);
        }

        &_stale {
            background: $global-very-gray-84;
        }
    }

    &__lock {
        position: absolute;
        right: -4px;
        bottom: -4px;
        width: 12px;
        height: 12px;
        display: grid;
        place-items: center;
        border: 1px solid $global-light-gray;
        border-radius: 50%;
        background: $global-white;
        color: $global-very-gray-84;
        fill: currentColor;
    }

    &__spin {
        display: block;
        width: 9px;
        height: 9px;
        border: 1.5px solid $global-very-gray-dd;
        border-top-color: $global-black;
        border-radius: 50%;
        animation: brand-support-overview-spin .7s linear infinite;
    }

    &__more {
        flex: none;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 22px;
        padding: 0 7px;
        border: 1px solid $global-light-gray;
        border-radius: 7px;
        background: $global-white;
        color: $global-dark-3a;
        font: inherit;
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;

        &:hover {
            border-color: $global-gray-light;
        }

        &.is-compact {
            padding: 0 3px 0 6px;
            fill: currentColor;
        }
    }

    &__icon {
        flex: none;
        width: 22px;
        height: 22px;
        display: grid;
        place-items: center;
        padding: 0;
        border: 0;
        border-radius: 7px;
        background: transparent;
        color: $global-very-gray-84;
        fill: currentColor;
        cursor: pointer;

        &:hover {
            background: $global-white-f6;
            color: $global-black;
        }
    }

    &__add {
        flex: none;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 22px;
        padding: 0 7px;
        border: 1px dashed $global-gray-light;
        border-radius: 7px;
        background: transparent;
        color: $global-very-gray-5b;
        fill: currentColor;
        font: inherit;
        font-size: 10px;
        cursor: pointer;
    }

    &__tip {
        position: fixed;
        z-index: 1000;
        max-width: 280px;
        padding: 8px 10px;
        border-radius: 8px;
        background: $global-very-dark;
        color: $global-white;
        font-size: 12px;
        line-height: 17px;
        pointer-events: none;

        b {
            display: block;
            font-size: 13px;
            font-weight: 600;
        }

        span {
            display: block;
            opacity: .8;

            &.is-warn {
                color: $global-yellow-light;
                opacity: 1;
            }
        }
    }

    &__menu {
        position: fixed;
        z-index: 1000;
        width: 300px;
        max-width: calc(100vw - 16px);
        padding: 6px;
        border: 1px solid $global-gray-light;
        border-radius: 12px;
        background: $global-white;
        box-shadow: 0 4px 8px rgba(58, 65, 70, .06), 0 8px 24px rgba(58, 65, 70, .06);
    }

    &__menu-list {
        display: grid;
        gap: 2px;
        max-height: 280px;
        overflow: auto;
    }

    &__menu-item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 8px 10px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: $global-black;
        font: inherit;
        text-align: left;
        cursor: pointer;

        &:hover {
            background: $global-white-f6;
        }

        &[aria-disabled="true"] {
            color: $global-very-gray-84;
            cursor: not-allowed;
        }
    }

    &__menu-name {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;

        small {
            display: block;
            color: $global-very-gray-84;
            font-size: 12px;
        }
    }

    &__menu-current {
        padding: 0 5px;
        border: 1px solid $global-gray-light;
        border-radius: 5px;
        color: $global-very-gray-5b;
        font-size: 11px;
    }

    &__menu-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-top: 6px;
        padding: 8px 8px 2px;
        border-top: 1px solid $global-light-gray;
        color: $global-very-gray-84;
        font-size: 12px;
    }
}

@keyframes brand-support-overview-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .brand-support-overview__spin {
        animation-duration: 2s;
    }
}
</style>
